import type { TeamId } from '@/tasks/task';
import { MenuIcon } from './MenuIcon';

/**
 * メニュー項目の識別子。
 */
export type MenuId =
  | 'tasks'
  | 'analysis'
  | 'fmt-monthly'
  | 'monthly-progress'
  | 'fmt-closing'
  | 'closing-progress'
  | 'related-links'
  | 'members-settings'
  | 'kinds-settings'
  | AutomationId;

/**
 * 自動化システムの識別子。
 *
 * それぞれ既存の自動化（GAS / GitHub Actions）に対応する。実行の繋ぎ込みは
 * タスク管理が固まったあとに行うため、いまは一覧と実行画面の枠だけを持つ。
 */
export type AutomationId =
  | 'auto-payment-prep'
  | 'auto-invoice-check'
  | 'auto-expense-check'
  | 'auto-amount-recon'
  | 'auto-payroll-csv'
  | 'auto-payment-schedule'
  | 'auto-subsidy-pl'
  | 'auto-tanshin'
  | 'auto-balance-recon'
  | 'auto-depreciation'
  | 'auto-sales-recalc';

/**
 * サイドバーのメニュー。グループ見出しごとに項目を持つ。
 *
 * 今後コックピット（自動化の実行ハブ）や設定を足すときは、グループを増やして
 * そこへ項目を並べる。並び順で迷わないよう、ここを唯一の定義にする。
 * @graph-connects none
 */
export const MENU_GROUPS: readonly {
  heading: string;
  /** このグループを出すチーム。未指定なら全チームで出す */
  teams?: readonly TeamId[];
  /** true ならサイドバーの最下部へ寄せる（設定など、普段使わないもの） */
  pinToBottom?: boolean;
  items: readonly { id: MenuId; label: string }[];
}[] = [
  {
    heading: 'タスク',
    items: [
      { id: 'tasks', label: 'タスク管理' },
      { id: 'analysis', label: 'タスク分析' },
    ],
  },
  {
    // 月次経理は SFT（ストラテジックファイナンスチーム）の担当領域。
    // 作業の型（FMT）と、その作業を置き換える自動化を同じセグメントに並べる。
    // 「毎月これをやる」という括りで一続きに見えるほうが辿りやすい
    heading: '月次経理',
    teams: ['sft'],
    items: [
      { id: 'fmt-monthly', label: 'FMT' },
      { id: 'monthly-progress', label: '進捗' },
      { id: 'auto-payment-prep', label: '支払準備' },
      { id: 'auto-invoice-check', label: 'AI請求書チェック' },
      { id: 'auto-expense-check', label: '経費チェック' },
      { id: 'auto-amount-recon', label: '金額突合' },
      { id: 'auto-payroll-csv', label: '給与CSV出力' },
      { id: 'auto-payment-schedule', label: '支払スケジュール' },
      { id: 'auto-subsidy-pl', label: '補助金PL共有' },
      { id: 'auto-balance-recon', label: '残高照合' },
      { id: 'auto-depreciation', label: '減価償却再計算' },
    ],
  },
  {
    // 決算（四半期・年次）も月次経理と同じく、作業の型・進捗・その作業を置き換える自動化を
    // 一続きに並べる。売上再計算は四半期の決算整理で回すので、ここに置く
    heading: '決算',
    teams: ['sft'],
    items: [
      { id: 'fmt-closing', label: 'FMT' },
      { id: 'closing-progress', label: '進捗' },
      { id: 'auto-sales-recalc', label: '売上再計算' },
    ],
  },
  {
    // 月次・決算のどちらの型にも入らない周期で回すもの
    heading: 'その他自動化',
    teams: ['sft'],
    items: [{ id: 'auto-tanshin', label: '短信作成' }],
  },
  {
    // 業務で開く外部のページ。埋め込むと重いので、開く先を並べるだけにする
    heading: 'リンク',
    items: [{ id: 'related-links', label: '関連リンク' }],
  },
  {
    // 設定は日常の操作ではないので最下部へ寄せる
    heading: '設定',
    pinToBottom: true,
    items: [
      { id: 'members-settings', label: 'メンバー設定' },
      { id: 'kinds-settings', label: '種別設定' },
    ],
  },
];

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business チームの略称を返す。チーム ID は TEAMS に必ずあるので、そのまま引く
 * @graph-connects none
 */
export function teamLabel(team: TeamId): string {
  return TEAMS.find((entry) => entry.id === team)?.label ?? team.toUpperCase();
}

/**
 * 切り替えられるチーム。
 * @graph-connects none
 */
export const TEAMS: readonly { id: TeamId; label: string; fullName: string }[] = [
  { id: 'sft', label: 'SFT', fullName: 'ストラテジックファイナンスチーム' },
  { id: 'cpt', label: 'CPT', fullName: 'コーポレートサポートチーム' },
];

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business そのメニューが自動化かを判定する。自動化の識別子は `auto-` で始めると決めている
 * @graph-connects none
 */
export function isAutomation(id: MenuId): boolean {
  return id.startsWith('auto-');
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 指定チームで表示するメニューグループだけに絞る
 * @graph-connects none
 */
export function visibleGroups(team: TeamId): typeof MENU_GROUPS {
  return MENU_GROUPS.filter((group) => group.teams === undefined || group.teams.includes(team));
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 指定チームで開けるメニューかを判定する。チームを切り替えたとき、隠れた画面に留まらないようにする
 * @graph-connects visibleGroups [calls] 表示対象のグループを引く
 */
export function isMenuAvailable(id: MenuId, team: TeamId): boolean {
  return visibleGroups(team).some((group) => group.items.some((item) => item.id === id));
}

/**
 * Sidebar の props。
 */
export interface SidebarProps {
  /** 選択中のメニュー */
  current: MenuId;
  /** メニューを選んだときに呼ばれる */
  onSelect: (id: MenuId) => void;
  /** 折りたたみ中か */
  collapsed: boolean;
  /** 折りたたみを切り替えるときに呼ばれる */
  onToggle: () => void;
  /** 選択中のチーム */
  team: TeamId;
  /** ホーム（チームを選ぶ画面）へ戻るときに呼ばれる */
  onGoHome: () => void;
}

/**
 * @graph-node {Page}
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 左側のサイドバー。表示する機能を選ぶ。上のホームでチームを選び直す画面へ戻れる。折りたたんで画面を広く使える
 * @graph-connects none
 */
export function Sidebar({ current, onSelect, collapsed, onToggle, team, onGoHome }: SidebarProps) {
  if (collapsed) {
    return (
      <aside className="flex w-11 shrink-0 flex-col items-center border-r border-[var(--color-border-subtle)] bg-[var(--color-bg-subtle)] py-4">
        <button
          type="button"
          aria-label="サイドバーを開く"
          onClick={onToggle}
          className="h-7 w-7 rounded border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] text-sm text-[var(--color-text-secondary)]"
        >
          &rsaquo;
        </button>

        {/* 折りたたみ中も、ホームへ戻れて「どのチームを見ているか」も分かるようにする */}
        <button
          type="button"
          onClick={onGoHome}
          title="ホーム（チームを選び直す）"
          className="mt-3 flex cursor-pointer flex-col items-center gap-0.5 rounded px-1 py-1 text-[10px] font-semibold text-[var(--color-text-muted)] hover:bg-[var(--color-bg-surface)]"
        >
          <MenuIcon id="home" className="opacity-60" />
          {teamLabel(team)}
        </button>
      </aside>
    );
  }

  return (
    <aside className="flex w-52 shrink-0 flex-col overflow-y-auto border-r border-[var(--color-border-subtle)] bg-[var(--color-bg-subtle)] px-3 py-4">
      <div className="mb-4 flex items-center justify-end">
        <button
          type="button"
          aria-label="サイドバーをしまう"
          onClick={onToggle}
          className="h-6 w-6 rounded border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] text-xs text-[var(--color-text-secondary)]"
        >
          &lsaquo;
        </button>
      </div>

      {/* ホームはメニューの上に置く。押すとチームを選ぶ画面へ戻る。
          どのチームを見ているかは画面全体の前提なので、機能の選択より先に目に入る位置に出す */}
      <div className="mb-4">
        <button
          type="button"
          onClick={onGoHome}
          className="flex w-full cursor-pointer items-center gap-2 rounded border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] px-2.5 py-2 text-left text-sm font-medium text-[var(--color-text-primary)] hover:bg-[var(--color-bg-subtle)]"
        >
          <MenuIcon id="home" className="opacity-60" />
          ホーム
        </button>
      </div>

      <nav className="flex flex-1 flex-col space-y-5">
        {visibleGroups(team)
          .filter((group) => group.pinToBottom !== true)
          .map((group) => (
            <div key={group.heading}>
              <h2 className="m-0 mb-1.5 px-1 text-xs font-bold tracking-wide text-[var(--color-text-secondary)]">
                {group.heading}
              </h2>
              <ul className="m-0 list-none space-y-1 p-0">
                {group.items.map((item) => (
                  <li key={item.id}>
                    <button
                      type="button"
                      aria-current={current === item.id}
                      onClick={() => {
                        onSelect(item.id);
                      }}
                      className={
                        current === item.id
                          ? 'w-full rounded bg-[var(--color-bg-surface)] px-2.5 py-2 text-left text-sm font-semibold text-[var(--color-text-primary)] shadow-sm'
                          : 'w-full rounded px-2.5 py-2 text-left text-sm text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-surface)]/60'
                      }
                    >
                      {/* 先頭に線画のアイコン。自動化はすべて稲妻にして、手でやる作業と
                          並んでいても見分けが付くようにする */}
                      <MenuIcon id={item.id} className="mr-2 opacity-60" />
                      {item.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}

        {/* 設定は下寄せ。上のグループとの間隔は自動で空く */}
        {visibleGroups(team)
          .filter((group) => group.pinToBottom === true)
          .map((group) => (
            <div
              key={group.heading}
              className="mt-auto border-t border-[var(--color-border-subtle)] pt-4"
            >
              <h2 className="m-0 mb-1.5 px-1 text-xs font-bold tracking-wide text-[var(--color-text-secondary)]">
                {group.heading}
              </h2>
              <ul className="m-0 list-none space-y-1 p-0">
                {group.items.map((item) => (
                  <li key={item.id}>
                    <button
                      type="button"
                      aria-current={current === item.id}
                      onClick={() => {
                        onSelect(item.id);
                      }}
                      className={
                        current === item.id
                          ? 'w-full rounded bg-[var(--color-bg-surface)] px-2.5 py-2 text-left text-sm font-semibold text-[var(--color-text-primary)] shadow-sm'
                          : 'w-full rounded px-2.5 py-2 text-left text-sm text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-surface)]/60'
                      }
                    >
                      <MenuIcon id={item.id} className="mr-2 opacity-60" />
                      {item.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
      </nav>
    </aside>
  );
}
